import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { ecNoOnlyWhiteSpacesValidator } from '@cf/core';
import {
  FormFieldComponent,
  InputDirective,
  InputErrorComponent,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { FormPhoneNumberComponent } from '@cf/shared/forms/phone-number';
import { CheckboxComponent } from '@cf/shared/forms/primitives';
import { localDataProvider, SelectNewComponent } from '@cf/shared/select';
import {
  BookingPageQuestionsAnswerTypeEnum,
  IBookingPageAnswer,
  SchemaAnswerTypeEnum,
  SchemaMeetingTemplateQuestion,
} from '@cf/temp/services';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { OTHER_ANSWER_ID } from '../../../../const/booking-questions-form.const';
import { IBookingQuestionFormGroup } from '../../../../interfaces/booking-form.interface';

@Component({
  selector: 'cf-question-field',
  templateUrl: './booking-question-field.component.html',
  styleUrls: ['./booking-question-field.component.scss'],
  imports: [
    FormFieldComponent,
    ReactiveFormsModule,
    LabelComponent,
    SelectNewComponent,
    InputDirective,
    NgxTolgeeModule,
    MatRadioGroup,
    MatRadioButton,
    FormPhoneNumberComponent,
    InputErrorComponent,
    NgTemplateOutlet,
    CheckboxComponent,
  ],
})
export class QuestionFieldComponent {
  question = input<SchemaMeetingTemplateQuestion>();
  questionForm = input<FormGroup<IBookingQuestionFormGroup>>();
  answerType = input<SchemaAnswerTypeEnum>();

  OTHER_ANSWER_ID = OTHER_ANSWER_ID;
  MeetingQuestionsAnswerTypeEnum = BookingPageQuestionsAnswerTypeEnum;

  translateService = inject(TranslateService);

  @Output() checkboxChange = new EventEmitter<{
    answer: IBookingPageAnswer;
    checked: boolean;
  }>();

  get answerOptions() {
    return localDataProvider(this.question()!.answers, {
      valueKey: 'id',
      labelKey: 'answer',
    });
  }

  get isOtherSelectedForAnswer() {
    return this.questionForm()!.controls.answer.value === OTHER_ANSWER_ID;
  }

  get isOtherSelectedForAnswers() {
    return this.questionForm()!.controls.answers.value.includes(OTHER_ANSWER_ID);
  }

  get hasAnswerError() {
    return (
      this.questionForm()!.controls.answer.touched && this.questionForm()!.controls.answer.invalid
    );
  }

  get hasAnswersError() {
    return (
      this.questionForm()!.controls.answers.touched && this.questionForm()!.controls.answers.invalid
    );
  }

  get hasAnswerTextError() {
    return (
      this.questionForm()!.controls.answer_text.touched &&
      this.questionForm()!.controls.answer_text.invalid
    );
  }

  onCheckboxChange(answer: IBookingPageAnswer, checked: boolean) {
    if (answer.id === undefined) {
      return;
    }

    const answersControl = this.questionForm()!.controls.answers;
    const value = answersControl.value;

    if (checked) {
      value.push(answer.id);
    } else {
      value.splice(value.indexOf(answer.id), 1);
    }

    answersControl.patchValue(value);

    const answerTextControl = this.questionForm()!.controls.answer_text;
    if (!value.includes(OTHER_ANSWER_ID)) {
      answerTextControl.patchValue('');
    }

    this.updateOtherOptionValidation(value.includes(OTHER_ANSWER_ID));
  }

  onOptionChange(value?: string | number) {
    const answerTextControl = this.questionForm()!.controls.answer_text;
    if (value !== OTHER_ANSWER_ID) {
      answerTextControl.patchValue('');
    }

    this.updateOtherOptionValidation(value === OTHER_ANSWER_ID);
  }

  updateOtherOptionValidation(otherOption: boolean) {
    const baseValidation = [Validators.maxLength(1000), ecNoOnlyWhiteSpacesValidator];
    this.questionForm()!.controls.answer_text.setValidators(
      this.question()?.required && otherOption
        ? [Validators.required, ...baseValidation]
        : baseValidation,
    );
  }
}
